import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import { Users, Search, X, Plus, Check } from 'lucide-react';
import Button from '../Button';
import Avatar from '../Avatar';
import UserName from '../UserName';
import supabase from '../../lib/supabase';

const schema = z.object({
  name: z.string().min(1, 'Group name is required'),
  initialMessage: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface GroupChatModalProps {
  onClose: () => void;
  onSuccess: (conversationId: string) => void;
}

export default function GroupChatModal({ onClose, onSuccess }: GroupChatModalProps) {
  const { user } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, display_name, verified, avatar_url')
        .or(`full_name.ilike.%${query}%,display_name.ilike.%${query}%`)
        .neq('id', user?.id)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectUser = (selectedUser: any) => {
    if (!selectedUsers.some(u => u.id === selectedUser.id)) {
      setSelectedUsers([...selectedUsers, selectedUser]);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Validate that at least 2 participants are selected
      if (selectedUsers.length < 2) {
        setError("Please select at least 2 participants");
        return;
      }

      // Create group chat using the RPC function
      const { data: result, error: createError } = await supabase
        .rpc('create_group_chat', {
          name: data.name,
          initial_message: data.initialMessage || null,
          member_ids: selectedUsers.map(u => u.id)
        });

      if (createError) throw createError;

      onSuccess(result);
    } catch (err: any) {
      console.error('Error creating group:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-20">
      <div className="bg-neutral rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-secondary border-b border-secondary p-4 flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold font-dillab text-primary">Create Group Chat</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-light rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          {error && (
            <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-xl">
              {error}
            </div>
          )}

          <div>
            <input
              type="text"
              {...register('name')}
              className="w-full h-12 px-4 rounded-full border border-secondary-light bg-secondary-light text-primary placeholder:text-primary focus:border-secondary-light focus:ring-0 focus:outline-none"
              placeholder="Enter group name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.name.message}</p>
            )}
          </div>

          <div>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                className="w-full h-12 px-4 pl-10 rounded-full border border-secondary-light bg-secondary-light text-primary placeholder:text-primary focus:border-secondary-light focus:ring-0 focus:outline-none"
                placeholder="Add Participants"
              />
            </div>

            {/* Search Results */}
            {searchQuery && (
              <div className="mt-2 bg-secondary-light rounded-xl shadow-elevation-2 overflow-hidden text-primary">
                {isSearching ? (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-accent mx-auto"></div>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="divide-y divide-neutral divide-opacity-10">
                    {searchResults.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleSelectUser(user)}
                        className="w-full p-3 flex items-center space-x-3 hover:bg-secondary transition-colors"
                      >
                        <Avatar
                          src={user.avatar_url}
                          alt={user.display_name || user.full_name}
                          size="sm"
                        />
                        <UserName
                          name={user.display_name || user.full_name}
                          verified={user.verified}
                          size="sm"
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-neutral">
                    No users found
                  </div>
                )}
              </div>
            )}

            {/* Selected Users */}
            {selectedUsers.length > 0 && (
              <div className="mt-2 space-y-2 pl-4">
                {selectedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between w-44 p-2 bg-secondary text-primary rounded-lg"
                  >
                    <div className="flex items-center space-x-2">
                      <Avatar
                        src={user.avatar_url}
                        alt={user.display_name || user.full_name}
                        size="sm"
                      />
                      <span className="text-sm">
                        {user.display_name || user.full_name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(user.id)}
                      className="p-1 text-neutral hover:text-secondary-dark rounded-full transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <textarea
              {...register('initialMessage')}
              className="w-full h-20 p-2 pl-4 rounded-full border border-secondary-light bg-secondary-light text-primary placeholder:text-primary focus:border-secondary-light focus:ring-0 focus:outline-none"
              placeholder="Send a message to get the conversation started..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 mt-4">
            <Button
              type="button"
              variant="primary"
              className="text-surface-light"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="text-surface-light"
              disabled={isSubmitting || selectedUsers.length < 2}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}